import { Person } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import { CustomTooltip } from "components/RosterConfig/RosterConfigStyle";
import SnackBar from "components/SnackBar/SnackBar";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { searchEmployeeProfile } from "services/userServices";
import { SnackBarStoreAction } from "Store/SnackBar";

const CustomPopper = (props) => {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={[
        {
          name: "flip",
          enabled: false,
        },
      ]}
    />
  );
};

const DEBOUNCE_DELAY = 800; // milliseconds
const MIN_SEARCH_LENGTH = 3;

export default function PeoplePicker(props) {
  const {
    onChange,
    onReset,
    placeholder = "Search by name, email, or Oracle ID",
    label = "Employee Search",
    defaultValue = null,
    isError = false,
    clearFields,
    disabled = false,
  } = props;

  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const searchTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const dispatch = useDispatch();

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // Show snackbar using Redux
  const showSnack = useCallback(
    (message, severity) => {
      const obj = {
        open: true,
        message: message,
        severity: severity,
      };
      dispatch(SnackBarStoreAction.updateSnack(obj));
    },
    [dispatch],
  );

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  // Handle clearing fields
  useEffect(() => {
    if (clearFields !== undefined) {
      setSearchResults([]);
      setSelectedEmployee(null);
      setInputValue("");
      setOpen(false);
    }
  }, [clearFields]);

  // Handle default value
  // Only the defaultValue useEffect needs changing.
  // Replace the existing one with this:

  useEffect(() => {
    if (!defaultValue) return;

    // ── Fast path: parent already resolved the employee object ──────────────
    // This is now the normal case from LDSupportModalContent / TASupportModalContent.
    // No API call needed at all.
    if (typeof defaultValue === "object") {
      setSelectedEmployee(defaultValue);
      setInputValue(defaultValue.Name || defaultValue.Display || "");
      return;
    }

    // ── Slow path: raw OracleId / name string passed directly ───────────────
    // Only reached when PeoplePicker is used standalone (e.g. BulkEdit inline cell).
    if (
      typeof defaultValue === "string" &&
      defaultValue.length >= MIN_SEARCH_LENGTH
    ) {
      performSearch(defaultValue, true);
    }
  }, [defaultValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Perform the actual search
  const performSearch = async (searchQuery, isDefaultSearch = false) => {
    if (!searchQuery || searchQuery.trim().length < MIN_SEARCH_LENGTH) {
      if (!isDefaultSearch) {
        setSearchResults([]);
        setOpen(false);
      }
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setLoading(true);
    setOpen(true);

    try {
      const searchResult = await searchEmployeeProfile(searchQuery.trim());

      if (searchResult.status === true && searchResult.data) {
        let searchResultDetails;

        // Check if search is by Oracle ID (numeric)
        if (!isNaN(searchQuery)) {
          searchResultDetails = searchResult.data.map((emp) => ({
            OracleId: emp.OracleId,
            Display:
              (emp.Name ?? "") + (emp.OracleId ? ` (${emp.OracleId})` : ""),
            Name: emp.Name,
            Email: emp.Email,
          }));
        } else {
          searchResultDetails = searchResult.data.map((emp) => ({
            OracleId: emp.OracleId,
            Display: emp.Display,
            Name: emp.Name,
            Email: emp.Email,
          }));
        }

        setSearchResults(searchResultDetails);

        // Auto-select if default search and single result
        if (isDefaultSearch && searchResultDetails.length === 1) {
          handleEmployeeSelect(null, searchResultDetails[0]);
          setOpen(false);
        } else if (!isDefaultSearch && searchResultDetails.length > 0) {
          showSnack(`Found ${searchResultDetails.length} result(s)`, "success");
        }
      } else {
        setSearchResults([]);
        if (!isDefaultSearch) {
          showSnack("No results found matching your search", "info");
        }
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
      if (!isDefaultSearch) {
        showSnack("Error occurred while searching. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // Debounced search handler
  const handleInputChange = (event, newInputValue, reason) => {
    setInputValue(newInputValue);

    // Clear search timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    // Handle clearing
    if (reason === "clear" || newInputValue === "") {
      setSearchResults([]);
      setSelectedEmployee(null);
      setOpen(false);
      if (onReset) {
        onReset();
      }
      return;
    }

    // Only search on input (not on select)
    if (reason === "input") {
      if (newInputValue.length < MIN_SEARCH_LENGTH) {
        setSearchResults([]);
        setOpen(false);
        return;
      }

      // Set debounced search
      searchTimerRef.current = setTimeout(() => {
        performSearch(newInputValue);
      }, DEBOUNCE_DELAY);
    }
  };

  // Handle employee selection
  const handleEmployeeSelect = (event, value) => {
    if (value) {
      setSelectedEmployee(value);
      setInputValue(value.Name || value.Display || "");
      setOpen(false);
      if (onChange) {
        onChange(value);
      }
    }
  };

  // Handle manual clear/reset
  const handleChange = (event, value, reason) => {
    if (reason === "clear") {
      setSelectedEmployee(null);
      setInputValue("");
      setSearchResults([]);
      setOpen(false);
      if (onReset) {
        onReset();
      }
    } else if (value) {
      handleEmployeeSelect(event, value);
    }
  };

  return (
    <>
      <FormControl fullWidth>
        <Autocomplete
          freeSolo
          disabled={disabled}
          options={searchResults}
          value={selectedEmployee}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={handleChange}
          open={open}
          onOpen={() => {
            if (searchResults.length > 0) {
              setOpen(true);
            }
          }}
          onClose={() => setOpen(false)}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            }
            return option.Display || option.Name || "";
          }}
          isOptionEqualToValue={(option, value) =>
            option.OracleId === value?.OracleId
          }
          filterOptions={(x) => x} // Disable client-side filtering
          loading={loading}
          loadingText="Searching..."
          noOptionsText={
            inputValue.length < MIN_SEARCH_LENGTH
              ? `Type at least ${MIN_SEARCH_LENGTH} characters to search`
              : "No employees found"
          }
          PopperComponent={CustomPopper}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.OracleId}>
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <Person
                  sx={{ mr: 1, color: "primary.main", fontSize: "1.2rem" }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.Name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {option.Email} • ID: {option.OracleId}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              size="small"
              variant="outlined"
              error={isError}
              helperText={
                isError
                  ? "Please select an employee"
                  : inputValue.length > 0 &&
                      inputValue.length < MIN_SEARCH_LENGTH
                    ? `Type ${MIN_SEARCH_LENGTH - inputValue.length} more character(s) to search`
                    : ""
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <CustomTooltip title="Search by name, email, or Oracle ID">
                      <Person color="action" />
                    </CustomTooltip>
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="primary" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormControl>
      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
}

PeoplePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isError: PropTypes.bool,
  clearFields: PropTypes.number,
  disabled: PropTypes.bool,
};
